import { ArrowLeft, FileText, Loader2 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import ApiClient from "@/lib/api-client";
import UnderstandingChart from "@/components/understanding-chart";
import LessonHeader from "@/components/lesson-header";
import UnderstandingSection from "@/components/understanding-section";
import RightPanel from "@/components/right-panel";
import {
  getUnderstandingLabel,
  getUnderstandingColor,
} from "@/lib/understanding-utils";
import {
  UnderstandingLevel,
  type GroupedStudent,
  type PreLessonMaterialValues,
  type BuildQuestionValues,
  FileUtils,
} from "@live-writing/models";

interface UnderstandingGroup {
  level: UnderstandingLevel;
  label: string;
  color: string;
  students: GroupedStudent[];
  count: number;
  teachingTips?: string[];
}

interface LessonOverviewData {
  lesson: {
    id: number;
    title: string;
    subject: string;
    topic: string;
    materials?: PreLessonMaterialValues[];
  };
  understanding_distribution: UnderstandingGroup[];
  total_students: number;
  questions?: BuildQuestionValues[];
}

function PreLessonOverview() {
  const { code } = useParams<{ code: string }>();
  const [data, setData] = useState<LessonOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

  const fetchData = async () => {
    if (!code) return;

    try {
      setLoading(true);
      const response = await ApiClient.get<LessonOverviewData>(
        `/pre-lesson/results/${code}`,
      );

      const transformedDistribution = response.understanding_distribution.map(
        (group) => ({
          ...group,
          label: getUnderstandingLabel(group.level),
          color: getUnderstandingColor(group.level),
        }),
      );

      setData({
        ...response,
        understanding_distribution: transformedDistribution,
      });
    } catch (err) {
      setError("Failed to load results");
      console.error("Error fetching results:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [code]);

  const handleMaterialClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    material: PreLessonMaterialValues,
    index: number,
  ) => {
    if (!FileUtils.isFileType(material.type) || !material.s3_key) {
      return;
    }

    e.preventDefault();
    setLoadingIndex(index);

    try {
      const response = await ApiClient.get<{ download_url: string }>(
        `/pre-lesson/material/download?s3_key=${encodeURIComponent(material.s3_key)}`,
      );
      window.open(response.download_url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to get download URL:", error);
    } finally {
      setLoadingIndex(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 mb-8" />
        <div className="h-32 bg-gray-100 rounded-lg mb-6" />
        <div className="h-48 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-[26px] font-bold text-foreground">
            Results Overview
          </h1>
        </div>
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {error || "No data available"}
          </p>
        </div>
      </div>
    );
  }

  const visibleGroups = data.understanding_distribution.filter(
    (group) => group.count > 0,
  );

  return (
    <div className="flex mx-auto gap-6">
      <div className="w-3/5">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Link
              to="/"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-[26px] font-bold text-foreground">
              Results Overview
            </h1>
          </div>
          <Button
            variant="outline"
            className="hidden bg-blue-500 hover:bg-blue-600 text-white border-blue-500"
          >
            View Pre-Lesson
          </Button>
        </div>

        <LessonHeader lesson={data.lesson} />

        {data.lesson.materials && data.lesson.materials.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 w-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Lesson Materials
            </h2>
            <div className="space-y-3">
              {data.lesson.materials.map((material, index) => (
                <a
                  key={index}
                  href={material.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-100 transition-colors ${
                    loadingIndex === index
                      ? "pointer-events-none opacity-70"
                      : ""
                  }`}
                  onClick={(e) => handleMaterialClick(e, material, index)}
                >
                  <FileText className="h-10 w-10 text-blue-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {material.title}
                    </p>
                  </div>
                  {loadingIndex === index && (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600 shrink-0" />
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        <UnderstandingChart data={data.understanding_distribution} />

        <div className="space-y-6">
          {visibleGroups.map((group) => (
            <UnderstandingSection key={group.level} group={group} />
          ))}
        </div>
      </div>
      <div className="w-2/5">
        <RightPanel preLessonId={data.lesson.id} questions={data.questions} />
      </div>
    </div>
  );
}

export default PreLessonOverview;
